import VueCookies from 'vue-cookies';
import store from '@/store/index';

function hasDashboardConfig() {
  return !!store.state.configurer.dashboardConfig;
}

function hasUploadedData() {
  const { rawExcelData, selectedSheet } = store.state.configurer;
  return !!selectedSheet && !!rawExcelData && Object.keys(rawExcelData).length > 0;
}

function hasMappedData() {
  const { columnMappingConfig, transformedData } = store.state.configurer;
  return !!columnMappingConfig && Array.isArray(transformedData) && transformedData.length > 0;
}

function getRequiredStepRedirect(step) {
  if (step >= 3 && !hasDashboardConfig()) return { name: 'configure-customize' };
  if (step >= 4 && !hasUploadedData()) return { name: 'configure-data' };
  if (step >= 5 && !hasMappedData()) return { name: 'configure-mapping' };
  if (step >= 7 && !store.getters['configurer/isConfigComplete']) {
    return { name: 'configure-settings' };
  }
  return null;
}

function configurerStepGuard(to, from, next) {
  const redirect = getRequiredStepRedirect(to.meta?.step || 0);
  if (redirect) return next(redirect);
  return next();
}

const routes = [
  {
    path: '/configure',
    name: 'configurer-landing',
    component: () => import('./layout.vue'),
    meta: {
      title: 'MSDAT Configurer',
    },
    children: [
      {
        path: 'account',
        name: 'configurer-account',
        beforeEnter: (to, from, next) => {
          const user = VueCookies.get('configurer-user');
          if (user) return next('/configure/user/customize');
          return next();
        },
        component: () => import('./account.vue'),
        meta: {
          title: 'MSDAT Configurer | Sign In',
          label: 'Create Account',
          step: 1,
        },
      },
      {
        path: 'user',
        name: 'configurer',
        redirect: '/configure/user/customize',
        beforeEnter: (to, from, next) => {
          const user = VueCookies.get('configurer-user');
          if (!user) return next('/configure/account');
          return next();
        },
        component: () => import('./authenticated.vue'),
        children: [
          {
            path: 'profile',
            name: 'configure-profile',
            component: () => import('./profile.vue'),
            meta: {
              title: 'MSDAT Configurer | My Dashboards',
              label: 'My Dashboards',
            },
          },
          {
            path: 'customize',
            name: 'configure-customize',
            component: () => import('./customize.vue'),
            meta: {
              title: 'MSDAT Configurer | Customize',
              label: 'Customize MSDAT',
              step: 2,
            },
          },
          {
            path: 'data',
            name: 'configure-data',
            beforeEnter: configurerStepGuard,
            component: () => import('./data.vue'),
            meta: {
              title: 'MSDAT Configurer | Data Upload',
              label: 'Data Upload',
              step: 3,
            },
          },
          {
            path: 'mapping',
            name: 'configure-mapping',
            beforeEnter: configurerStepGuard,
            component: () => import('./columnMapping.vue'),
            meta: {
              title: 'MSDAT Configurer | Column Mapping',
              label: 'Column Mapping',
              step: 4,
            },
          },
          {
            path: 'appearance',
            name: 'configure-appearance',
            beforeEnter: configurerStepGuard,
            component: () => import('./appearance.vue'),
            meta: {
              title: 'MSDAT Configurer | Appearance',
              label: 'Appearance',
              step: 5,
            },
          },
          {
            path: 'settings',
            name: 'configure-settings',
            beforeEnter: configurerStepGuard,
            component: () => import('./settings.vue'),
            meta: {
              title: 'MSDAT Configurer | Settings',
              label: 'Settings',
              step: 6,
            },
          },
          {
            path: 'preview',
            name: 'configure-preview',
            beforeEnter: configurerStepGuard,
            component: () => import('./preview.vue'),
            meta: {
              title: 'MSDAT Configurer | Preview',
              label: 'Preview',
              step: 7,
            },
          },
        ],
      },
    ],
  },
  // Standalone Dynamic Dashboard route (outside configurer layout - no MSDAT header/footer)
  {
    path: '/generated-dashboard/:instanceId',
    name: 'generated-dashboard',
    component: () => import('./DynamicDashboard.vue'),
    meta: {
      title: 'Dashboard', // will be overwritten dynamically by the component
      hideLayout: true,
    },
  },
];

function mapSteps(rts) {
  const steps = [];
  rts.forEach((route) => {
    if (route.meta?.step) steps.push(route.meta);
    if (route.children) steps.push(...mapSteps(route.children));
  });
  return steps;
}

export const steps = mapSteps(routes);

// Set document.title from route meta on every navigation
export function applyRouterTitleGuard(router) {
  router.afterEach((to) => {
    const title = to.meta?.title;
    if (title) document.title = title;
  });
}

export default routes;
